import API from '../../../utils/API';

/**
 * ProgramFormManager class to handle form-related operations for programs
 * This separates form editing and saving logic from the overall program configuration
 */
class ProgramFormManager {
    constructor(component) {
        this.component = component;

        const { programId, orgId } = component.props;
        this.programId = programId;
        this.orgId = orgId;


        // Bind methods to maintain context
        this.saveForm = this.saveForm.bind(this);
        this.cancelEdit = this.cancelEdit.bind(this);
        this.toggleFormEditorModal = this.toggleFormEditorModal.bind(this);
        this.updateForm = this.updateForm.bind(this);
    }

    /**
     * Saves the current form with validation and error handling
     */
    async saveForm() {
        const { orgId, programId } = this.component.props;
        const { editedForm, selectedSection } = this.component.state;

        try {
            // Validate form structure
            if (!editedForm?.pages?.length) {
                throw new Error('Form must have at least one page');
            }

            if (!editedForm.pages[0]?.sections?.length) {
                throw new Error('Form must have at least one section');
            }

            // Validate required fields in each section
            const invalidFields = [];
            editedForm.pages[0].sections.forEach((section, sectionIndex) => {
                section.fields.forEach((field, fieldIndex) => {
                    if (!field.label?.trim()) {
                        invalidFields.push(`Section ${sectionIndex + 1}, Field ${fieldIndex + 1}: Missing label`);
                    }
                    if (field.required && !field.type) {
                        invalidFields.push(`Section ${sectionIndex + 1}, Field ${fieldIndex + 1}: Required field missing type`);
                    }
                });
            });

            if (invalidFields.length > 0) {
                throw new Error(`Form validation failed:\n${invalidFields.join('\n')}`);
            }

            // Map section types to form keys and modal states
            const formTypes = {
                checkInForm: {
                    key: 'checkInForm',
                    modalKey: 'checkinFormEditorModalOpen'
                },
                walkInForm: {
                    key: 'walkInForm',
                    modalKey: 'walkInFormEditorModalOpen'
                },
                sessionCheckIn: {
                    key: 'sessionCheckInForm',
                    modalKey: 'sessionCheckInFormEditorModalOpen'
                }
            };

            const config = formTypes[selectedSection];
            if (!config) {
                throw new Error(`Invalid section type: ${selectedSection}`);
            }

            // Prepare form data
            const formData = {
                ...editedForm,
                confirmationEmail: editedForm.confirmationEmail === 'N/A' || editedForm.confirmationEmail === '' ? undefined : editedForm.confirmationEmail,
                formClosedTitle: editedForm.formClosedTitle || '',
                formClosedMessage: editedForm.formClosedMessage || '',
                lastModified: new Date().toISOString()
            };
            delete formData.submissions;
            // Save form
            const response = await API().patch(
                `Organizations/${orgId}/programs/${programId}/checkIn`,
                {
                    [config.key]: formData,
                }
            );

            if (!response.data) {
                throw new Error('No data received from server');
            }

            const updatedForm = response.data[config.key];

            // Create deep copy to avoid reference issues
            const formCopy = JSON.parse(JSON.stringify(updatedForm));

            // Update state with new form data
            this.component.setState(prevState => ({
                checkInAppConfiguration: {
                    ...prevState.checkInAppConfiguration,
                    [config.key]: formCopy
                },
                //[config.modalKey]: false,
                editedForm: formCopy,
                form: formCopy,
                editingFormBuilder: false,
                formEditorTabSelected: 0,
                focusOnField: false,
                focusedFieldId: null,
                focusedSectionId: null,
                editCount: prevState.editCount + 1,
                error: null
            }));

            // No need to save the entire app configuration here
            // The form is already updated in the state
        } catch (error) {
            console.error('Failed to save form:', error);
            this.component.setState({
                error: error.message || 'Failed to save form. Please try again.',
                invalid: true,
                submitting: false
            });
        }
    }

    /**
     * Cancels the current form edit operation
     */
    cancelEdit() {
        const { selectedSection } = this.component.state;

        // Map section types to form keys and modal states
        const formTypeMap = {
            checkInForm: {
                formKey: 'checkInForm',
                modalKey: 'checkinFormEditorModalOpen'
            },
            walkInForm: {
                formKey: 'walkInForm',
                modalKey: 'walkInFormEditorModalOpen'
            },
            sessionCheckIn: {
                formKey: 'sessionCheckInForm',
                modalKey: 'sessionCheckInFormEditorModalOpen'
            }
        };

        const config = formTypeMap[selectedSection];
        if (config) {
            // Reset form state and close modal
            this.component.setState({
                editedForm: null,
                form: null,
                editing: false,
                editingFormBuilder: false,
                [config.modalKey]: false,
                error: null
            });
        }
    }

    /**
     * Toggles the form editor modal with proper form type handling
     */
    async toggleFormEditorModal() {
        const { orgId, programId } = this.component.props;
        const { checkInAppConfiguration, selectedSection } = this.component.state;

        // Standardized mapping between section IDs and form configurations
        const formTypeMap = {
            checkInForm: {
                form: checkInAppConfiguration.checkInForm,
                property: 'checkInForm',
                name: 'Event Check-In',
                modalKey: 'checkinFormEditorModalOpen'
            },
            walkInForm: {
                form: checkInAppConfiguration.walkInForm,
                property: 'walkInForm',
                name: 'Walk-In Registration',
                modalKey: 'walkInFormEditorModalOpen'
            },
            sessionCheckIn: {
                form: checkInAppConfiguration.sessionCheckInForm,
                property: 'sessionCheckInForm',
                name: 'Session Check-In',
                modalKey: 'sessionCheckInFormEditorModalOpen'
            }
        };

        // Get the current tab selection from the component's state
        const currentTab = this.component.state.selectedSection;
        // Determine which form type to use based on the current tab
        let formType;
        if (currentTab === 'checkInForm') {
            formType = 'checkInForm';
        } else if (currentTab === 'walkInForm') {
            formType = 'walkInForm';
        } else if (currentTab === 'sessionCheckIn') {
            formType = 'sessionCheckInForm';
        } else {
            console.error('Invalid tab selection:', currentTab);
            return;
        }

        const config = formTypeMap[currentTab];
        if (!config) {
            console.error('Invalid form type:', formType);
            return;
        }

        try {
            if (!config.form) {
                // Create new form with initial structure
                const payload = {
                    name: config.name,
                    formType: 'registration',

                };

                const response = await API().patch(
                    `Organizations/${orgId}/programs/${programId}/checkIn`,
                    {
                        [formType]: payload,
                    }
                );

                if (response.data) {
                    const form = response.data[formType];
                    const formCopy = JSON.parse(JSON.stringify(form));

                    // Update configuration with new form
                    const updatedConfig = {
                        ...checkInAppConfiguration,
                        [config.property]: formCopy
                    };

                    // Save configuration first
                    await this.component.handleChange(updatedConfig, true);
                    await this.component.saveCheckInAppConfiguration();

                    // Open form editor modal with the new form
                    this.component.setState({
                        [config.modalKey]: true,
                        editedForm: formCopy,
                        form: formCopy,
                        editCount: this.component.state.editCount + 1,
                        error: null
                    });
                }
            } else {
                // Edit existing form - create deep copy to avoid reference issues
                const formCopy = JSON.parse(JSON.stringify(config.form));

                // Open form editor modal with the existing form
                this.component.setState({
                    [config.modalKey]: true,
                    editedForm: formCopy,
                    form: formCopy,
                    error: null
                });
            }
        } catch (error) {
            console.error('Failed to handle form editor modal:', error);
            this.component.setState({
                error: 'Failed to load form editor. Please try again.'
            });
        }
    }

    toggleSubmissionModal(formType) {
        const { checkInAppConfiguration } = this.component.state;
        const form = checkInAppConfiguration[formType];
        if (!form) {
            console.error('Invalid form type:', formType);
            return;
        }

        this.component.setState(prevState => ({
            formSubmissionModalOpen: !prevState.formSubmissionModalOpen,
            form,
            formType
        }));
    }

    /**
     * Updates a form based on its type (checkin, walkin, or session)
     */
    updateForm(formType, form) {
        // Standardized mapping between form types and their configurations
        const formTypeMap = {
            checkInForm: {
                property: 'checkInForm',
                modalKey: 'checkinFormEditorModalOpen',
                section: 'checkInForm'
            },
            walkInForm: {
                property: 'walkInForm',
                modalKey: 'walkInFormEditorModalOpen',
                section: 'walkInForm'
            },
            sessionCheckIn: {
                property: 'sessionCheckInForm',
                modalKey: 'sessionCheckInFormEditorModalOpen',
                section: 'sessionCheckIn'
            }
        };

        const config = formTypeMap[formType];
        if (!config) {
            console.error('Invalid form type:', formType);
            return;
        }

        try {
            // Create deep copy to avoid reference issues
            const formCopy = JSON.parse(JSON.stringify(form));

            // Validate form structure
            if (!formCopy?.pages?.[0]?.sections) {
                formCopy.pages = [{
                    title: 'Check In',
                    subtitle: '',
                    sections: [{
                        _id: `section_${Date.now()}`,
                        title: '',
                        fields: [],
                        buttons: []
                    }]
                }];
            }

            // Update state with proper form references
            this.component.setState(prevState => ({
                checkInAppConfiguration: {
                    ...prevState.checkInAppConfiguration,
                    [config.property]: formCopy
                },
                editedForm: formCopy,
                form: formCopy,
                editingFormBuilder: true,
                [config.modalKey]: true,
                selectedSection: config.section,
                editCount: prevState.editCount + 1,
                error: null
            }));
        } catch (error) {
            console.error('Failed to update form:', error);
            this.component.setState({
                error: 'Failed to update form. Please try again.'
            });
        }
    }
}

export default ProgramFormManager;
